import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import { fmtTime12 } from '../../utils/time';
import { codeLabel } from '../../utils/format';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };

const TASK_COLS = [
  { key:'client_name',  label:'Client' },
  { key:'task_name',    label:'Task Name' },
  { key:'mandatory',    label:'Mandatory',   render: v => v ? <span className="text-amber-600 text-xs font-semibold">Yes</span> : <span className="text-slate-400 text-xs">No</span> },
  { key:'completion',   label:'Completed',   render: v => v ? <span className="text-green-600 text-xs font-semibold">Yes</span> : <span className="text-red-500 text-xs">No</span> },
  { key:'log_date',     label:'Log Date',    render: fmtDate },
  { key:'intended_time',label:'Intended Time',render: v => v ? fmtTime12(v) : '–' },
  { key:'logged_by',    label:'Logged By' },
  { key:'notes',        label:'Notes',       render: v => v ? <span className="text-xs text-slate-500 max-w-[200px] truncate block">{v}</span> : '–' },
];

const GOAL_COLS = [
  { key:'client_name', label:'Client' },
  { key:'goal_title',  label:'Goal' },
  { key:'category',    label:'Category',    render: v => codeLabel(v) || '–' },
  { key:'progress',    label:'Progress',    render: v => {
    const pct = Math.min(100, Math.max(0, v||0));
    const color = pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500';
    return <span className={clsx('font-semibold text-sm', color)}>{pct}%</span>;
  }},
  { key:'status',      label:'Status',      render: v => <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 capitalize">{v||'–'}</span> },
  { key:'target_date', label:'Target Date', render: fmtDate },
  { key:'plan_name',   label:'Plan' },
  { key:'log_date',    label:'Log Date',    render: fmtDate },
  { key:'notes',       label:'Notes',       render: v => v ? <span className="text-xs text-slate-500 max-w-[160px] truncate block">{v}</span> : '–' },
];

export default function CarePlanActivity() {
  const [viewTab, setViewTab]   = useState('tasks');
  const [taskData, setTaskData] = useState([]);
  const [goalData, setGoalData] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 14), 'yyyy-MM-dd'));
  const [endDate, setEnd]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const taskPag = usePagination(taskData);
  const goalPag = usePagination(goalData);

  const load = useCallback(() => {
    setLoading(true);
    const p = `start_date=${startDate}&end_date=${endDate}`;
    Promise.all([
      api.get(`/reports/care-plan-activity/tasks?${p}`),
      api.get(`/reports/care-plan-activity/goals?${p}`),
    ])
      .then(([t, g]) => {
        setTaskData(t.data||[]);
        setGoalData(g.data||[]);
        taskPag.reset();
        goalPag.reset();
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const activeData = viewTab === 'tasks' ? taskData : goalData;
  const chips = [{ label:'Date Range', value:`${startDate} - ${endDate}` }];

  return (
    <ReportLayout title="Care Plan & Goal Activity Log" data={activeData} filename={`care_plan_activity_${viewTab}`} columns={viewTab === 'tasks' ? TASK_COLS : GOAL_COLS}
      activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {['tasks','goals'].map(t => (
          <button key={t} onClick={() => setViewTab(t)}
            className={clsx('px-5 py-2 text-sm font-medium rounded-lg transition-all capitalize',
              viewTab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
            {t}
          </button>
        ))}
      </div>

      {viewTab === 'tasks'
        ? <ReportTable columns={TASK_COLS} data={taskData} loading={loading} pagination={taskPag}/>
        : <ReportTable columns={GOAL_COLS} data={goalData} loading={loading} pagination={goalPag}/>
      }
    </ReportLayout>
  );
}
